import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { FleetMap } from '@/components/pmv/map';
import type { FleetMapMarkerInput, FleetMapGeofenceInput } from '@/components/pmv/map';
import pmvService from '@/services/pmv.service';
import { useProject } from '@/hooks/useProjects';
import { formatProjectOption } from '@/utils/pmvDisplay';
import { explainNullUtilization } from '@/utils/pmvUxCopy';
import { formatLastSeen } from '@/utils/pmvConnectedCopy';

const { Title, Text, Paragraph } = Typography;

const DEPLOYED_ISH = new Set(['ACTIVE', 'RECEIVED', 'ON_SITE', 'DEPLOYED', 'OPERATING']);

function categoryTag(cat: string) {
  switch (cat) {
    case 'OBSERVED_INSIDE':
      return <Tag color="success">Observed on site</Tag>;
    case 'OBSERVED_OUTSIDE':
      return <Tag color="warning">Observed outside</Tag>;
    case 'IN_TRANSIT':
      return <Tag color="processing">In transit</Tag>;
    default:
      return <Tag>No recent location</Tag>;
  }
}

const ProjectPmvPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectQ = useProject(id ?? '');
  const [mapError, setMapError] = useState<string | null>(null);
  const [fitToken, setFitToken] = useState(0);

  const reqQ = useQuery({
    queryKey: ['pmv-project-requests', id],
    queryFn: () => pmvService.listRequests({ project_id: id, pageSize: 100 }),
    enabled: Boolean(id),
  });

  const allocQ = useQuery({
    queryKey: ['pmv-project-allocations', id],
    queryFn: () => pmvService.listAllocations({ project_id: id, pageSize: 100 }),
    enabled: Boolean(id),
  });

  const logsQ = useQuery({
    queryKey: ['pmv-project-logs', id],
    queryFn: () => pmvService.listDailyLogs({ project_id: id, pageSize: 100 }),
    enabled: Boolean(id),
  });

  const spatialQ = useQuery({
    queryKey: ['pmv-project-spatial', id],
    queryFn: () => pmvService.getProjectSpatialMap(id!),
    enabled: Boolean(id),
    refetchInterval: 60_000,
    retry: false,
  });

  const componentAttnQ = useQuery({
    queryKey: ['pmv-component-attention-project', id],
    queryFn: () => pmvService.listComponentAttention({ limit: 200 }),
    enabled: Boolean(id),
    staleTime: 60_000,
    retry: false,
  });

  const requests = reqQ.data?.data ?? [];
  const allocations = allocQ.data?.data ?? [];
  const logs = logsQ.data?.data ?? [];
  const certified = logs.filter((l) => l.status === 'CERTIFIED');
  const deployedCount = allocations.filter((a) =>
    DEPLOYED_ISH.has(String(a.allocation_status || '').toUpperCase())
  ).length;

  const allocatedProfileIds = useMemo(() => {
    const ids = new Set<string>();
    for (const a of allocations as Array<{ pmv_profile_id?: string }>) {
      if (a.pmv_profile_id) ids.add(String(a.pmv_profile_id));
    }
    return ids;
  }, [allocations]);

  const projectComponentAttention = useMemo(() => {
    const items = componentAttnQ.data?.items ?? [];
    return items.filter((i) => i.pmv_profile_id && allocatedProfileIds.has(i.pmv_profile_id));
  }, [componentAttnQ.data, allocatedProfileIds]);
  const tyreAttn = projectComponentAttention.filter((i) => i.family === 'TYRE').length;
  const batAttn = projectComponentAttention.filter((i) => i.family === 'BATTERY').length;

  const today = new Date().toISOString().slice(0, 10);
  const todayLogs = logs.filter((l) => l.operating_date === today);
  const todayCertified = todayLogs.filter((l) => l.status === 'CERTIFIED').length;
  const missingToday =
    todayLogs.length > 0 ? todayLogs.length - todayCertified : Math.max(0, deployedCount - todayCertified);

  let working = 0;
  let available = 0;
  for (const l of certified) {
    working += l.working_minutes;
    available += l.available_minutes;
  }

  const utilPct = available > 0 ? Math.round((working / available) * 1000) / 10 : null;
  const projectLabel = projectQ.data ? formatProjectOption(projectQ.data) : 'Project equipment';

  const spatial = spatialQ.data;
  const mapMarkers: FleetMapMarkerInput[] = useMemo(
    () =>
      (spatial?.markers ?? [])
        .filter((m) => m.location)
        .map((m) => ({
          id: m.pmv_profile_id,
          lat: m.location!.lat,
          lon: m.location!.lon,
          label: m.fleet_number || m.asset_code || m.pmv_profile_id.slice(0, 8),
          freshness: m.location!.freshness,
          connectivity: m.connectivity,
          movement: m.movement_state,
          hasAttention: m.attention.open_count > 0,
        })),
    [spatial?.markers]
  );
  const mapGeofences: FleetMapGeofenceInput[] = useMemo(
    () =>
      (spatial?.geofences ?? []).map((g) => ({
        id: g.id,
        name: g.name || g.code,
        type: g.type,
        geometry_type: g.geometry_type,
        center_lat: g.center_lat,
        center_lon: g.center_lon,
        radius_m: g.radius_m,
        geometry_json: g.geometry_json as FleetMapGeofenceInput['geometry_json'],
      })),
    [spatial?.geofences]
  );

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Project equipment"
          documentNumber={projectQ.data?.project_number ?? ''}
          statusTag={<Tag color="blue">Plant &amp; Fleet</Tag>}
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={5} style={{ margin: 0 }}>
            {projectLabel}
          </Title>
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/control-tower')}>Command Center</Button>
            <Button onClick={() => navigate('/asset/pmv/components/control-tower')}>
              Component Tower
            </Button>
            <Button onClick={() => navigate(`/asset/pmv/connected/map`)}>Live Fleet Map</Button>
            <Button type="primary" onClick={() => navigate(`/asset/pmv/requests/new`)}>
              New equipment request
            </Button>
          </Space>
        </Space>

        {(tyreAttn > 0 || batAttn > 0) && (
          <Alert
            type="warning"
            showIcon
            message="Component attention on allocated units"
            description={
              <Space direction="vertical" size={4}>
                <Text>
                  {[
                    tyreAttn > 0 ? `${tyreAttn} tyre(s)` : null,
                    batAttn > 0 ? `${batAttn} battery/ies` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}{' '}
                  on this project’s allocations
                </Text>
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => navigate('/asset/pmv/components/control-tower')}
                >
                  Open Component Control Tower
                </Button>
              </Space>
            }
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic title="Requests" value={requests.length} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic title="Allocations" value={allocations.length} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic title="Deployed" value={deployedCount} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="Certified logs"
                value={certified.length}
                suffix={`/ ${logs.length}`}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic title="Missing logs (today)" value={missingToday} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="Utilization"
                value={utilPct != null ? utilPct : '—'}
                suffix={utilPct != null ? '%' : ''}
              />
              {utilPct != null ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  From certified logs only
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {explainNullUtilization(Math.max(missingToday, logs.length - certified.length))}
                </Text>
              )}
            </Card>
          </Col>
        </Row>

        <Card
          title="Equipment map (spatial evidence)"
          size="small"
          extra={
            <Space>
              <Button size="small" onClick={() => setFitToken((t) => t + 1)}>
                Fit map
              </Button>
              <Button
                size="small"
                onClick={() => navigate('/asset/pmv/connected/movement-attention')}
              >
                Spatial attention
              </Button>
            </Space>
          }
        >
          {spatialQ.isError ? (
            <Alert
              type="info"
              showIcon
              message="Spatial map unavailable"
              description="Connected Fleet may be disabled or you lack map permissions. Operational allocation tables below remain authoritative."
            />
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Alert
                type="info"
                showIcon
                message="Assigned ≠ observed inside"
                description={
                  spatial?.summary.note ||
                  'Location is evidence only. Stale or poor-quality signals are not counted as on site.'
                }
              />
              <Row gutter={[12, 12]}>
                <Col xs={12} sm={8} md={4}>
                  <Statistic title="Assigned" value={spatial?.summary.assigned ?? '—'} loading={spatialQ.isLoading} />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Statistic
                    title="Observed on site"
                    value={spatial?.summary.observed_on_site ?? '—'}
                    loading={spatialQ.isLoading}
                  />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Statistic
                    title="Observed outside"
                    value={spatial?.summary.observed_outside ?? '—'}
                    loading={spatialQ.isLoading}
                  />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Statistic
                    title="No recent location"
                    value={spatial?.summary.no_recent_location ?? '—'}
                    loading={spatialQ.isLoading}
                  />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Statistic title="Moving" value={spatial?.summary.moving ?? '—'} loading={spatialQ.isLoading} />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Statistic
                    title="Attention"
                    value={spatial?.summary.attention ?? '—'}
                    loading={spatialQ.isLoading}
                  />
                </Col>
              </Row>
              {mapError ? (
                <Alert type="warning" showIcon message="Map tiles unavailable" description={mapError} />
              ) : null}
              <FleetMap
                markers={mapMarkers}
                geofences={mapGeofences}
                height={360}
                fitToken={fitToken}
                onMapError={setMapError}
                onSelect={(mid) => navigate(`/asset/pmv/${mid}`)}
              />
              <Table
                size="small"
                rowKey="pmv_profile_id"
                loading={spatialQ.isLoading}
                pagination={{ pageSize: 10, size: 'small' }}
                dataSource={spatial?.equipment ?? []}
                locale={{ emptyText: 'No assigned equipment with spatial context' }}
                columns={[
                  {
                    title: 'Unit',
                    render: (_: unknown, r) =>
                      r.asset_name || r.fleet_number || r.asset_code || r.pmv_profile_id.slice(0, 8),
                  },
                  {
                    title: 'Spatial',
                    dataIndex: 'category',
                    render: (v: string) => categoryTag(v),
                  },
                  { title: 'Allocation', dataIndex: 'allocation_status', render: (v: string) => <Tag>{v}</Tag> },
                  { title: 'Movement', dataIndex: 'movement_state' },
                  {
                    title: 'Last position',
                    render: (_: unknown, r) =>
                      r.location?.observed_at ? formatLastSeen(r.location.observed_at) : '—',
                  },
                  {
                    title: '',
                    render: (_: unknown, r) => (
                      <Button type="link" onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}`)}>
                        Passport
                      </Button>
                    ),
                  },
                ]}
              />
              {(spatial?.unexpected_observed?.length ?? 0) > 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message={`${spatial!.unexpected_observed.length} unit(s) observed inside project geofence but not assigned`}
                  description="Does not imply allocation. Review equipment / geofence / attention workflows."
                />
              ) : null}
            </Space>
          )}
        </Card>

        <Card title="Demand" size="small">
          <Table
            rowKey="id"
            loading={reqQ.isLoading}
            dataSource={requests}
            pagination={false}
            locale={{ emptyText: 'No equipment requests for this project.' }}
            columns={[
              {
                title: 'Request',
                dataIndex: 'request_number',
                render: (v: string, r) => (
                  <Button type="link" onClick={() => navigate(`/asset/pmv/requests/${r.id}`)}>
                    {v}
                  </Button>
                ),
              },
              { title: 'Status', dataIndex: 'status', render: (v) => <Tag>{v}</Tag> },
            ]}
          />
        </Card>

        <Card title="Allocated equipment" size="small">
          <Table
            rowKey="id"
            loading={allocQ.isLoading}
            dataSource={allocations}
            pagination={false}
            locale={{ emptyText: 'No allocations on this project yet.' }}
            columns={[
              { title: 'Allocation', dataIndex: 'allocation_number' },
              { title: 'Status', dataIndex: 'allocation_status', render: (v) => <Tag>{v}</Tag> },
              { title: 'From', dataIndex: 'planned_start' },
              { title: 'Until', dataIndex: 'planned_end' },
              {
                title: '',
                render: (_, r) => (
                  <Space>
                    <Button type="link" onClick={() => navigate(`/asset/pmv/allocations/${r.id}`)}>
                      Open allocation
                    </Button>
                    {(r as { pmv_profile_id?: string }).pmv_profile_id ? (
                      <Button
                        type="link"
                        onClick={() =>
                          navigate(
                            `/asset/pmv/${(r as { pmv_profile_id: string }).pmv_profile_id}?tab=components`
                          )
                        }
                      >
                        Components
                      </Button>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>

        <Card title="Field operations" size="small">
          <Table
            rowKey="id"
            loading={logsQ.isLoading}
            dataSource={logs}
            pagination={false}
            locale={{ emptyText: 'No daily equipment logs for this project.' }}
            columns={[
              { title: 'Log', dataIndex: 'log_number' },
              { title: 'Date', dataIndex: 'operating_date' },
              { title: 'Status', dataIndex: 'status', render: (v) => <Tag>{v}</Tag> },
              {
                title: '',
                render: (_, r) => (
                  <Button type="link" onClick={() => navigate(`/asset/pmv/daily-logs/${r.id}`)}>
                    Open daily log
                  </Button>
                ),
              },
            ]}
          />
        </Card>

        <Card title="Cost" size="small">
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Operational attribution — PMV does not post GL. Fuel and internal-hire costs on this
            project appear as operational attribution only.
          </Paragraph>
        </Card>
      </Space>
    </WorkspaceLayout>
  );
};

export default ProjectPmvPage;
